import { SafeHtml } from '@angular/platform-browser'
import { Type, Expose, Serializable, plainToClass, stripTags, Transform } from '@tokeet-frontend/tv3-platform'
import { Attachment } from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'

export enum MessageType {
  Draft = -1,
  Inbound = 1,
  Outbound = 2,
  System = 3,
  Guest = 4,
  Owner = 5,
}

// TV4-6337: an Airbnb reaction, with the first name and role of the person who reacted.
export interface MessageReaction {
  emoji: string
  user_id?: string
  name?: string
  role?: string
  updated_at?: number
}

export interface MessageAttributes {
  aiResponder?: {
    language: string
    summary: string
  }
}

export class Message extends Serializable<Message> {
  @Expose({ name: 'pkey' })
  id: string

  @Expose({ name: 'reference_id' })
  referenceId: number

  @Expose({ name: 'guest_name' })
  guestName: string

  @Expose({ name: 'message_id' })
  messageId: string

  @Expose({ name: 'message_html' })
  messageHtml: string

  @Expose({ name: 'processed_date' })
  processedDate: number

  @Expose({ name: 'rental_id' })
  rentalId: string

  @Expose({ name: 'rental_name' })
  rentalName: string

  @Expose({ name: 'guest_id' })
  guestId: string

  @Expose({ name: 'message_txt' })
  messageTxt: string

  @Expose({ name: 'convo_id' })
  convoId: string

  @Expose({ name: 'received_on' })
  receivedOn: number

  @Expose({ name: 'guest_email' })
  guestEmail: string

  @Expose({ name: 'inquiry_id' })
  inquiryId: string

  @Expose({ name: 'replyto' })
  replyTo: string

  @Expose({ name: 'sentto' })
  sentTo: string

  @Expose({ name: 'external_user_id' })
  externalUserId: string

  @Expose({ name: 'edited_at' })
  editedAt: number

  @Expose({ name: 'unsent_at' })
  unsentAt: number

  @Expose({ name: 'read_at' })
  readAt: number

  // TV4-6323: the text that the guest sees after Airbnb removed a phone number from a host message.
  @Expose({ name: 'airbnb_text' })
  airbnbText: string

  reactions: MessageReaction[]

  @Expose({ name: 'sent_with' })
  @Transform((value) => value && String(value).replace('-twilio', ''))
  sentWith: 'automata' | 'tokeet'

  written_by: string

  cc: string[]
  bcc: string[]

  name: string
  type: MessageType
  archived: number
  from: string
  account: number
  read: number
  created: number
  attributes?: MessageAttributes
  delivery_message?: string

  @Type(() => Attachment)
  attachments: Attachment[]

  innerAttachments: boolean

  touch: number
  subject: string
  template: string

  @Expose({ name: 'tax_fee' })
  taxFee: number

  @Expose({ name: 'guest_arrive' })
  guestArrive: string

  @Expose({ name: 'guest_depart' })
  guestDepart: string

  discounts: number
  currency: string
  sum: number
  charge: number

  delivery_status: 'accepted' | 'rejected' | 'delivered' | 'failed' | 'opened' | 'clicked' | 'sent' | 'sending'

  sanitizedMsgHtml: SafeHtml

  // The text to show for an Airbnb message without text: the unsent note, the video status or the file name.
  statusText: string

  isProcessed = false
  isSending = false

  static deserialize(data: any): Message {
    const message: Message = plainToClass<Message, Message>(Message, data)
    // @ts-ignore
    message.type = message.type * 1 // convert to number
    // TV4-6337: an Airbnb message without text shows a status text, not its subject ("Staying at <listing>").
    const isAirbnb = !!message.externalUserId || !!message.unsentAt
    message.messageTxt = lodash.toString(
      message.messageTxt || stripTags(message.messageHtml) || (isAirbnb ? '' : message.subject) || ''
    )
    if (isAirbnb && !message.messageTxt) {
      message.statusText = airbnbStatusText(message)
    }
    message.messageHtml = message.messageHtml || message.messageTxt.replace(/\n/g, '<br>')
    message.receivedOn = parseInt((<any>message).receivedOn, 10)

    return message
  }
}

function airbnbStatusText(msg: Message): string {
  if (msg.unsentAt) {
    return $localize`This message was unsent`
  }
  let attachments: any = msg.attachments
  try {
    attachments = typeof attachments === 'string' ? JSON.parse(attachments) : attachments
  } catch (e) {}
  return lodash.get(attachments, [0, 'name']) || $localize`<Empty Content>`
}

export function isMessageDelivered(msg: Message) {
  return msg.delivery_status !== 'rejected' && msg.delivery_status !== 'failed'
}

export function isBotMessage(msg: Message) {
  const bot = /aiResponder|TokeetAI/i
  return bot.test(msg?.sentWith) || bot.test(msg?.written_by)
}
